/**
 * Persisted stale-while-revalidate cache (localStorage).
 *
 * - Entries live under `at:cache:v2:<userId>:<key>`; bump VERSION to drop old data
 *   (v2: exercises carry activity-type fields; v3: calendar days carry `exerciseTypes`;
 *   v4: profile carries `weeklyGoalMinutes` / `weeklyMinutes`;
 *   v5: workout exercises may carry `newRecords`; records cached under `records:<id>`;
 *   older versions are removed on load).
 * - `setCacheUser` wipes everything when the user changes, `clearCache` on logout,
 *   so one user's data is never shown to another.
 * - `createCachedResource` is a drop-in for `createResource`: it renders the
 *   cached value at once, revalidates in the background and swaps data in only
 *   when the JSON differs. `loading` is true only while there is nothing to show.
 * - Mutations call `invalidate(prefix)`: matching entries are removed, in-flight
 *   fetches are discarded and mounted resources refetch.
 */
import { createComputed, createSignal, onCleanup, untrack } from "solid-js";
import type { Accessor } from "solid-js";

const VERSION = "v5";
const PREFIX = `at:cache:${VERSION}:`;
const UID_KEY = `${PREFIX}uid`;

/** Remove entries written by older cache versions (their shapes lack newer fields). */
function dropOtherVersions() {
  const s = storage();
  if (!s) return;
  try {
    const stale: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k?.startsWith("at:cache:") && !k.startsWith(PREFIX)) stale.push(k);
    }
    for (const k of stale) s.removeItem(k);
  } catch {
    /* ignore */
  }
}
dropOtherVersions();

const DAY = 86_400_000;
export const TTL = { default: 7 * DAY, long: 30 * DAY } as const;

interface Entry<T> {
  v: T;
  t: number;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readUid(): string | null {
  try {
    return storage()?.getItem(UID_KEY) ?? null;
  } catch {
    return null;
  }
}

// Restored from storage so the cache is usable before the profile is fetched.
const [cacheUser, setCacheUserSignal] = createSignal<string | null>(readUid());

/** Id of the user the cache currently belongs to (reactive). */
export { cacheUser };

function storageKeys(): string[] {
  const s = storage();
  if (!s) return [];
  const keys: string[] = [];
  try {
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
  } catch {
    /* ignore */
  }
  return keys;
}

function removeKeys(keys: string[]) {
  const s = storage();
  if (!s) return;
  for (const k of keys) {
    try {
      s.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

function fullKey(key: string, uid = cacheUser()): string | null {
  return uid ? `${PREFIX}${uid}:${key}` : null;
}

/** Bind the cache to a user; data of any other user is dropped. */
export function setCacheUser(id: string) {
  if (cacheUser() !== id) {
    clearCache();
    try {
      storage()?.setItem(UID_KEY, id);
    } catch {
      /* ignore */
    }
    setCacheUserSignal(id);
  }
}

/** Logout / user switch: remove every cached entry and forget the user. */
export function clearCache() {
  removeKeys(storageKeys());
  for (const f of inflight.values()) f.stale = true;
  inflight.clear();
  setCacheUserSignal(null);
}

/** Skip background revalidation for data fetched this recently (e.g. start-up prefetch). */
const FRESH_MS = 5_000;

export function readCache<T>(key: string, ttl: number = TTL.default): T | undefined {
  return readEntry<T>(key, ttl)?.v;
}

function readEntry<T>(key: string, ttl: number = TTL.default): Entry<T> | undefined {
  const fk = fullKey(key);
  if (!fk) return undefined;
  try {
    const raw = storage()?.getItem(fk);
    if (!raw) return undefined;
    const e = JSON.parse(raw) as Entry<T>;
    if (!e || typeof e.t !== "number" || Date.now() - e.t > ttl) {
      removeKeys([fk]);
      return undefined;
    }
    return e;
  } catch {
    return undefined;
  }
}

export function writeCache<T>(key: string, value: T) {
  const fk = fullKey(key);
  const s = storage();
  if (!fk || !s) return;
  const raw = JSON.stringify({ v: value, t: Date.now() } satisfies Entry<T>);
  try {
    s.setItem(fk, raw);
  } catch {
    // Quota: drop all cached data (tokens/other keys are not touched), retry once.
    removeKeys(storageKeys().filter((k) => k !== UID_KEY));
    try {
      s.setItem(fk, raw);
    } catch {
      /* too big for storage — run uncached */
    }
  }
}

// ── in-flight dedupe + invalidation ─────────────────────────────────────────

interface Flight {
  promise: Promise<unknown>;
  stale: boolean;
}
const inflight = new Map<string, Flight>();
const listeners = new Set<(prefix: string) => void>();

/**
 * Fetch `key` once at a time (concurrent callers share the request) and persist
 * the result. A result invalidated while in flight is returned but not cached.
 */
export function cachedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const uid = cacheUser();
  const id = `${uid}:${key}`;
  const hit = inflight.get(id);
  if (hit) return hit.promise as Promise<T>;
  const flight: Flight = { promise: Promise.resolve(), stale: false };
  flight.promise = fetcher()
    .then((v) => {
      if (!flight.stale && cacheUser() === uid) writeCache(key, v);
      return v;
    })
    .finally(() => {
      if (inflight.get(id) === flight) inflight.delete(id);
    });
  inflight.set(id, flight);
  return flight.promise as Promise<T>;
}

/**
 * Cache-first read for imperative code: returns the cached value at once and
 * revalidates in the background; waits for the network only on a miss.
 */
export function cachedValue<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = TTL.default,
): Promise<T> {
  const hit = readCache<T>(key, ttl);
  const fresh = cachedFetch(key, fetcher);
  if (hit === undefined) return fresh;
  fresh.catch(() => undefined);
  return Promise.resolve(hit);
}

/** Drop cached entries whose key starts with any prefix and refetch live resources. */
export function invalidate(...prefixes: string[]) {
  const uid = cacheUser();
  if (!uid) return;
  for (const p of prefixes) {
    removeKeys(storageKeys().filter((k) => k.startsWith(`${PREFIX}${uid}:${p}`)));
    for (const [id, f] of inflight) {
      if (id.startsWith(`${uid}:${p}`)) {
        f.stale = true;
        inflight.delete(id);
      }
    }
  }
  for (const p of prefixes) for (const fn of [...listeners]) fn(p);
}

/** Run `fn` whenever a prefix is invalidated (auto-removed with the owner). */
export function onInvalidate(fn: (prefix: string) => void) {
  listeners.add(fn);
  onCleanup(() => listeners.delete(fn));
}

// ── resource ────────────────────────────────────────────────────────────────

export interface CachedResourceOptions<S, T> {
  key: (source: S) => string;
  fetch: (source: S) => Promise<T>;
  ttl?: number;
}

export type CachedResource<T> = Accessor<T | undefined> & {
  /** True only while there is no value to show yet (no cache hit, fetch pending). */
  readonly loading: boolean;
  readonly error: unknown;
};

function same(a: unknown, b: unknown) {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

/**
 * `createResource`-shaped helper with a persisted cache.
 * `source` returning false/null/undefined disables it (no data, no request).
 */
export function createCachedResource<S, T>(
  source: Accessor<S | false | null | undefined>,
  opts: CachedResourceOptions<S, T>,
): [
  CachedResource<T>,
  { refetch: () => Promise<void>; mutate: (value: T) => void },
] {
  const [data, setData] = createSignal<T | undefined>(undefined);
  const [fetching, setFetching] = createSignal(false);
  const [error, setError] = createSignal<unknown>(undefined);
  let seq = 0;
  let current: { s: S; key: string } | null = null;

  async function run(s: S, key: string) {
    const token = ++seq;
    setFetching(true);
    try {
      const v = await cachedFetch(key, () => opts.fetch(s));
      if (token !== seq) return;
      if (!same(v, untrack(data))) setData(() => v);
      setError(undefined);
    } catch (e) {
      if (token === seq) setError(e);
    } finally {
      if (token === seq) setFetching(false);
    }
  }

  createComputed(() => {
    const s = source();
    const uid = cacheUser();
    seq++;
    if (s === false || s == null || !uid) {
      current = null;
      setData(undefined);
      setFetching(false);
      return;
    }
    const key = untrack(() => opts.key(s));
    current = { s, key };
    setError(undefined);
    const hit = readEntry<T>(key, opts.ttl);
    setData(() => hit?.v);
    if (hit && Date.now() - hit.t < FRESH_MS) {
      setFetching(false);
      return;
    }
    untrack(() => void run(s, key));
  });

  onCleanup(() => {
    seq++;
    current = null;
  });

  onInvalidate((prefix) => {
    if (current?.key.startsWith(prefix)) void run(current.s, current.key);
  });

  const accessor = (() => data()) as CachedResource<T>;
  Object.defineProperties(accessor, {
    loading: { get: () => data() === undefined && fetching() },
    error: { get: () => error() },
  });

  return [
    accessor,
    {
      refetch: async () => {
        if (current) await run(current.s, current.key);
      },
      mutate: (value: T) => {
        setData(() => value);
        if (current) writeCache(current.key, value);
      },
    },
  ];
}
